"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { ShieldCheck } from "lucide-react";
import { authLogin, authMeProbe, getMaintenanceStatus } from "@/lib/api";
import { MaintenancePage } from "@/components/maintenance-page";

const LOGIN_GATED = process.env.NEXT_PUBLIC_LOGIN_GATED === "true";
const ACCESS_STORAGE_KEY = "xd_access_key";
const RETURN_TO_STORAGE_KEY = "xd_return_to";
const RETRY_FLAG_KEY = "xd_auth_state_retry";
const BINDING_STORAGE_KEY = "xd_oauth_binding";
// Bjóða vini: read back by the registration gate (registration-gate.tsx).
const REFERRAL_STORAGE_KEY = "xd_referral_code";
const REFERRAL_CODE_RE = /^[a-z0-9]{6,16}$/;

// Routes that match the canvasser deep-link pattern (a coordinator-shared
// link to a specific walk-list canvass/call surface). When `return_to`
// matches this, we render a banner explaining the user is opening a list
// for a specific election campaign.
const CANVASS_ROUTE_RE = /^\/elections\/[^/]+\/lists\/[^/]+\/(canvass|call)$/;

export default function LoginPage() {
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maintenance, setMaintenance] = useState<{ enabled: boolean; message: string } | null>(null);
  const [accessKey, setAccessKey] = useState<string | null>(null);
  const [gateChecked, setGateChecked] = useState(!LOGIN_GATED);
  const [returnTo, setReturnTo] = useState<string | null>(null);
  // Synchronous guard so a sub-frame double-tap on mobile can't fire
  // handleLogin twice before React commits `disabled={isLoading}`.
  const isRunning = useRef(false);
  // Ensure auto-submit-after-retry fires at most once per mount.
  const didAutoRetry = useRef(false);

  useEffect(() => {
    getMaintenanceStatus().then(setMaintenance).catch(() => {});
  }, []);

  // Session auto-recovery: a fully signed-in member can still land here — a
  // cross-site link doesn't transmit our SameSite=Strict cookies, so the edge
  // gate bounces the (cookie-less) document request to /login even though the
  // session is alive; the edge also sends expired-token holders here rather
  // than /dashboard. This probe runs same-site, so it DOES carry the cookies
  // (refreshing transparently if the access token lapsed). If it resolves,
  // skip the needless Kenni round-trip and return to the destination.
  //
  // Gated on the xd_has_refresh marker (non-httpOnly, path="/"): without a
  // refresh family there is nothing to recover, so genuinely logged-out
  // visitors (and the cookie-less e2e browser) skip the probe entirely.
  useEffect(() => {
    const hasRefreshMarker =
      typeof document !== "undefined" &&
      document.cookie.split("; ").includes("xd_has_refresh=1");
    if (!hasRefreshMarker) return;
    let cancelled = false;
    authMeProbe()
      .then((me) => {
        if (cancelled || !me) return;
        const stored = sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
        const dest =
          stored && stored.startsWith("/") && !stored.startsWith("//") && !stored.startsWith("/login")
            ? stored
            : "/dashboard";
        window.location.replace(dest);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Read access key from URL or sessionStorage
  useEffect(() => {
    if (!LOGIN_GATED) return;
    const paramKey = searchParams.get("access");
    if (paramKey) {
      sessionStorage.setItem(ACCESS_STORAGE_KEY, paramKey);
      setAccessKey(paramKey);
    } else {
      const stored = sessionStorage.getItem(ACCESS_STORAGE_KEY);
      if (stored) setAccessKey(stored);
    }
    setGateChecked(true);
  }, [searchParams]);

  // Persist `return_to` so the post-Kenni callback page can redirect there
  // even though Kenni's round trip drops everything but `code` + `state`.
  // Only accept relative paths to avoid open-redirect.
  useEffect(() => {
    const raw = searchParams.get("return_to");
    if (raw && raw.startsWith("/") && !raw.startsWith("//")) {
      sessionStorage.setItem(RETURN_TO_STORAGE_KEY, raw);
      setReturnTo(raw);
      return;
    }
    const stored = sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    if (stored) setReturnTo(stored);
  }, [searchParams]);

  // Bjóða vini: a shared link is `/?ref=<code>`, which the edge gate bounces
  // here as `/login?return_to=/?ref=<code>`. Kenni's round trip drops it and
  // `/` redirects to /dashboard without the query, so park the code for the
  // registration gate to send with /auth/register. localStorage, not
  // sessionStorage: on a phone Kenni may hand the callback to a new tab.
  useEffect(() => {
    let ref = searchParams.get("ref");
    const raw = searchParams.get("return_to");
    if (!ref && raw) {
      try {
        ref = new URL(raw, window.location.origin).searchParams.get("ref");
      } catch {
        ref = null;
      }
    }
    const code = ref?.trim().toLowerCase();
    if (!code || !REFERRAL_CODE_RE.test(code)) return;
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, code);
    } catch {}
  }, [searchParams]);

  const isDeepLinkArrival = returnTo != null && CANVASS_ROUTE_RE.test(returnTo);

  async function handleLogin() {
    // Synchronous guard — `disabled={isLoading}` only takes effect after
    // React commits, so a rapid double-tap can otherwise fire two parallel
    // login requests. Server-side Redis state makes the second cookie-
    // free, but stacking parallel /login calls is still wasteful.
    if (isRunning.current) return;
    isRunning.current = true;
    try {
      setIsLoading(true);
      setError(null);
      const { redirect_url, binding_token } = await authLogin(accessKey || undefined);
      // Validate redirect URL points to expected OAuth provider
      try {
        const url = new URL(redirect_url);
        const allowed = ["kenni.is", "www.kenni.is", "idp.kenni.is"];
        if (!allowed.includes(url.hostname)) {
          throw new Error("Unexpected redirect domain");
        }
      } catch {
        isRunning.current = false;
        setIsLoading(false);
        setError("Ógild beining frá auðkenningarþjónustu.");
        return;
      }
      // Stash the CSRF binding_token in sessionStorage (per-tab, same-origin
      // protected) so /callback can echo it back to prove the browser that
      // finishes the flow is the one that started it. Without this, any
      // attacker with a valid state+code pair could log a victim in as
      // themselves just by making the victim visit /callback?...
      try {
        sessionStorage.setItem(BINDING_STORAGE_KEY, binding_token);
      } catch {
        isRunning.current = false;
        setIsLoading(false);
        setError("Vafrinn þinn leyfir ekki sessionStorage. Prófaðu án einkaskoðunar.");
        return;
      }
      // Keep isLoading=true / isRunning=true — the page is about to unload,
      // and resetting would briefly re-enable the button for a ghost tap.
      window.location.href = redirect_url;
    } catch (err) {
      isRunning.current = false;
      setIsLoading(false);
      setError(
        err instanceof Error
          ? err.message
          : "Villa kom upp við innskráningu. Reyndu aftur."
      );
    }
  }

  // Auto-submit after a callback-side state failure. The callback page
  // sets `xd_auth_state_retry=1` then redirects here; we re-fire the
  // login flow so the user experiences a single hiccup instead of
  // landing on the login page and having to click the button again.
  // The flag is cleared up front so a re-failure shows the error page.
  useEffect(() => {
    if (!gateChecked) return;
    if (didAutoRetry.current) return;
    if (LOGIN_GATED && !accessKey) return;
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(RETRY_FLAG_KEY) !== "1") return;
    didAutoRetry.current = true;
    sessionStorage.removeItem(RETRY_FLAG_KEY);
    handleLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateChecked, accessKey]);

  if (maintenance?.enabled) {
    return <MaintenancePage message={maintenance.message} />;
  }

  if (!gateChecked) return null;

  // Gated mode: no valid access key — show closed message
  if (LOGIN_GATED && !accessKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 px-4">
        <div className="w-full max-w-sm text-center">
          <Image src="/xd-logo-blue.svg" alt="Sjálfstæðisflokkurinn" width={98} height={66} unoptimized priority className="h-12 w-auto mx-auto mb-8" />
          <div className="bg-card border border-border p-8">
            <h1 className="text-xl font-heading font-bold text-foreground">
              Kerfið er í undirbúningi
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Félagatorg Sjálfstæðisflokksins verður opnað á næstu vikum.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/50 px-4">
      <div className="w-full max-w-sm">
        {/* Logo above card */}
        <div className="text-center mb-8">
          <Image src="/xd-logo-blue.svg" alt="Sjálfstæðisflokkurinn" width={98} height={66} unoptimized priority className="h-12 w-auto mx-auto" />
        </div>

        {/* Main card */}
        <div className="bg-card border border-border p-8">
          <div className="text-center mb-6">
            <h1 className="text-xl font-heading font-bold text-foreground">
              Innskráning
            </h1>
            <p className="text-muted-foreground mt-1.5 text-sm">
              Skráðu þig inn með rafrænum skilríkjum
            </p>
          </div>

          {isDeepLinkArrival && (
            <div className="mb-5 p-3 bg-primary/8 border border-primary/20 text-foreground text-sm">
              <p className="font-semibold">
                Þú ert að opna vinnulista fyrir kosningabaráttu.
              </p>
              <p className="text-muted-foreground mt-0.5 text-xs">
                Skráðu þig inn með Kenni til að halda áfram.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-5 p-3 bg-destructive/8 border border-destructive/15 text-destructive text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="xd-btn xd-btn-primary w-full text-sm py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="xd-spinner !w-4 !h-4 !border-white/30 !border-t-white" />
                Tengist...
              </span>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                Skrá mig inn
              </>
            )}
          </button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Vefsíðan er fyrir félagsmenn Sjálfstæðisflokksins og stuðningsmenn D-lista.
        </p>
      </div>
    </div>
  );
}
